//通知中心
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { observer } from "mobx-react-lite";
import styled from "~/ui/styled";
import {
  BetweenFlex,
  CenterFlex,
  ColumnFlex,
  IconFont,
  StartFlex,
  Text,
  Button,
} from "~components/El";
import { useTheme } from "~/ui/styled";
import { useStores } from "~models";
import { globalToast, formatMessageTime, globalSpinner } from "~utils";
import {
  ActivityIndicator,
  DeviceEventEmitter,
  Platform,
  SectionList,
  SectionListData,
} from "~/ui/primitives";
import { useModal } from "~hooks/useModal";
import { NotificationCenterContent } from "~components/PopupComponents/DialogContent";
import { useChatList } from "~hooks/useChatList";
import { RedpacketStatus, TransferStatus } from "~proto";

const Container = styled(ColumnFlex)`
  flex: 1;
  width: auto;
  margin: ${({ theme }) => `${theme.space.sm}`};
`;

type NoticeStatus = "unread" | "readed";

type NoticeItem = {
  extra?: any;
  id: string | number;
  type?: number;
  label: string;
  content?: string;
  created?: string;
  status: NoticeStatus;
  is_read?: boolean;
};

type NoticeSection = {
  type: "order" | "transfer" | "system";
  label: string;
  icon: string;
  data: NoticeItem[];
};

const getConfirmText = (item: NoticeItem, data?: any) => {
  if (
    [2, 4].includes(item.type) &&
    item?.extra?.redpacket_id &&
    data?.status === RedpacketStatus.ACTIVE
  ) {
    return "领取红包";
  } else if (
    [2, 4].includes(item.type) &&
    item.extra?.order_no &&
    data?.status === TransferStatus.PENDING
  ) {
    return "转账确认";
  } else {
    return "标记已读";
  }
};

const NoticeRow = ({
  item,
  theme,
  icon,
  isFirst,
  isLast,
  onClick,
}: {
  item: NoticeItem;
  theme: any;
  icon: string;
  isFirst: boolean;
  isLast: boolean;
  onClick: () => void;
}) => {
  return (
    <CenterFlex
      width="auto"
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      style={{
        height: 62,
        backgroundColor:
          item.status === "unread"
            ? theme.colors.primary + "1a"
            : theme.colors.blockBackground,
        paddingRight: 10,
        borderTopLeftRadius: isFirst ? 8 : 0,
        borderTopRightRadius: isFirst ? 8 : 0,
        borderBottomLeftRadius: isLast ? 8 : 0,
        borderBottomRightRadius: isLast ? 8 : 0,
        overflow: "hidden",
      }}
      onClick={onClick}
    >
      <StartFlex
        style={{
          padding: 10,
          width: "auto",
          height: "auto",
          borderLeftWidth: 4,
          borderLeftColor:
            item.status === "unread" ? theme.colors.primary : "transparent",
          borderLeftStyle: "solid",
        }}
      >
        <CenterFlex
          height="auto"
          style={{
            width: "auto",
            borderRadius: 9999,
            padding: 6,
            backgroundColor:
              item.status === "unread"
                ? theme.colors.primary + "2a"
                : theme.colors.background,
          }}
        >
          <IconFont
            name={icon}
            size={18}
            color={
              item.status === "unread"
                ? theme.colors.primary
                : theme.colors.secondary
            }
          />
        </CenterFlex>
      </StartFlex>
      <BetweenFlex
        height="100%"
        width="auto"
        style={{
          borderBottomWidth: 1,
          borderBottomColor: isLast ? "transparent" : theme.colors.border,
          borderBottomStyle: "solid",
          flex: 1,
          height: "100%",
        }}
      >
        <ColumnFlex height={'auto'} justifyContent="center" paddingRight="10px">
          <BetweenFlex height="auto">
            <Text
              style={{
                fontWeight: "600",
                width: "auto",
                flex: 1,
                color:
                  item.status === "unread"
                    ? theme.colors.text
                    : theme.colors.infoText,
                fontSize: 14,
                lineHeight: 24,
              }}
            >
              {item.label}
            </Text>
            <Text color={theme.colors.infoText}>
              {formatMessageTime(item.created)}
            </Text>
          </BetweenFlex>
          {!!item.content && (
            <Text
              style={{
                width: "auto",
                fontSize: 12,
                lineHeight: 24,
                color:
                  item.status === "unread"
                    ? theme.colors.text
                    : theme.colors.secondaryText,
              }}
            >
              {item.content}
            </Text>
          )}
        </ColumnFlex>
        <CenterFlex height="auto" width="40px">
          <IconFont name="right" size={14} color={theme.colors.secondaryText} />
        </CenterFlex>
      </BetweenFlex>
    </CenterFlex>
  );
};
const NoticeCenter: React.FC = () => {
  const { apiStore, notificationStore } = useStores();
  const theme = useTheme();
  const { Dialog } = useModal();
  const { openRedPacketNoticePopup, openTransferReceiveNoticePopup } =
    useChatList();

  const PAGE_SIZE = 20;
  const [rawList, setRawList] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const detailRef = useRef<any>(null);

  const iconMap: Record<NoticeSection["type"], string> = useMemo(
    () => ({ order: "jytz", transfer: "hb", system: "xttz" }),
    [],
  );

  const normalizeItem = useCallback((item: any): NoticeItem => {
    const id = item?.id ?? null;
    const label = item?.title ?? "通知";
    const content = item?.content ?? "";
    const created = item?.created ?? "";
    const is_read = item?.is_read ?? false;
    const status: NoticeStatus = is_read ? "readed" : "unread";
    return {
      id,
      type: item?.type,
      label,
      content,
      created,
      status,
      is_read,
      extra: item?.extra,
    };
  }, []);

  const fetchPage = useCallback(
    async ({ nextPage, replace }: { nextPage: number; replace: boolean }) => {
      const setBusy = replace ? setRefreshing : setLoadingMore;
      setBusy(true);
      try {
        const res = await apiStore.getUserNotificationList({
          params: { page: nextPage, limit: PAGE_SIZE },
        });
        // console.log("--res--", res)
        const result = Array.isArray(res?.data) ? res.data : [];
        setHasMore(result.length >= PAGE_SIZE);
        setPage(nextPage);
        setRawList((prev) => {
          const base = replace ? [] : prev;
          const merged = [...base, ...result];
          // 去重（避免重复分页 / 刷新后追加）
          const seen = new Set<string | number>();
          return merged.filter((x) => {
            const key =
              x?.id ??
              x?._id ??
              `${x?.type ?? "unknown"}-${x?.created_at ?? x?.created ?? ""}-${
                x?.title ?? x?.label ?? ""
              }`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        });
      } catch (err: any) {
        // 失败时必须关掉 hasMore，否则空列表会反复触发 onEndReached连续请求
        setHasMore(false);
        globalToast.show(err?.message || "获取通知列表失败");
      } finally {
        setBusy(false);
      }
    },
    [PAGE_SIZE, apiStore],
  );

  useEffect(() => {
    fetchPage({ nextPage: 1, replace: true });
  }, [fetchPage]);

  useEffect(() => {
    const sub = DeviceEventEmitter.addListener(
      "RefreshNoticeCenterList",
      (list?: unknown) => {
        if (Array.isArray(list)) {
          setHasMore(list.length >= PAGE_SIZE);
          setPage(1);
          setRawList(list);
          return;
        }
        setHasMore(true);
        fetchPage({ nextPage: 1, replace: true });
      },
    );
    return () => sub.remove();
  }, [PAGE_SIZE, fetchPage]);

  const sections: NoticeSection[] = useMemo(() => {
    const normalized = rawList.map(normalizeItem);
    const order = normalized.filter((x) => [1, 3].includes(Number(x.type)));
    const transfer = normalized.filter((x) => [2, 4].includes(Number(x.type)));
    const system = normalized.filter((x) => [5].includes(Number(x.type)));

    return [
      {
        type: "order",
        label: "交易订单通知",
        icon: iconMap.order,
        data: order,
      },
      {
        type: "transfer",
        label: "待领取红包及转账通知",
        icon: iconMap.transfer,
        data: transfer,
      },
      {
        type: "system",
        label: "系统信息通知",
        icon: iconMap.system,
        data: system,
      },
    ];
  }, [iconMap.order, iconMap.system, iconMap.transfer, normalizeItem, rawList]);

  const onRefresh = useCallback(() => {
    if (refreshing) return;
    setHasMore(true);
    fetchPage({ nextPage: 1, replace: true });
  }, [fetchPage, refreshing]);

  const onEndReached = useCallback(() => {
    if (refreshing || loadingMore || !hasMore) return;
    fetchPage({ nextPage: page + 1, replace: false });
  }, [fetchPage, hasMore, loadingMore, page, refreshing]);

  const onMarkAsRead = useCallback(
    async (item: NoticeItem) => {
      if ([2, 4].includes(item.type) && item.extra?.redpacket_id) {
        globalSpinner.show();
        apiStore
          .getRedpacketDetail({ redpacket_id: item.extra.redpacket_id })
          .then(async (res) => {
            detailRef.current = res.data;
            globalSpinner.close();
            const pid = await Dialog.show({
              cancelText: "关闭",
              confirmText: getConfirmText(item, detailRef.current),
              confirmOnAction: item.is_read === false,
              autoClose: detailRef.current.status !== RedpacketStatus.ACTIVE,
              content: React.createElement(NotificationCenterContent, {
                ...item,
              }),
              onConfirm: async () => {
                if (detailRef.current.status === RedpacketStatus.ACTIVE) {
                  await openRedPacketNoticePopup({
                    redpacketData: detailRef.current,
                  });
                }
                const res = await apiStore.markNotificationAsRead({
                  id: item.id,
                });
                if (res) {
                  notificationStore.refreshUserUnreadNotificationCount();
                  fetchPage({ nextPage: 1, replace: true });
                }
              },
              onCancel: () => {
                Dialog.close(pid);
              },
            });
          })
          .catch((err: any) => {
            globalSpinner.close();
            globalToast.show(err?.message || "获取红包详情失败");
          });
      } else if ([2, 4].includes(item.type) && item.extra?.order_no) {
        globalSpinner.show();
        apiStore
          .getTransferDetail({ order_no: item.extra.order_no })
          .then(async (res) => {
            detailRef.current = res.data;
            globalSpinner.close();
            const pid = await Dialog.show({
              cancelText: "关闭",
              confirmText: getConfirmText(item, detailRef.current),
              confirmOnAction: item.is_read === false,
              autoClose: detailRef.current.status !== TransferStatus.PENDING,
              content: React.createElement(NotificationCenterContent, {
                ...item,
              }),
              onConfirm: async () => {
                if (detailRef.current.status === TransferStatus.PENDING) {
                  await openTransferReceiveNoticePopup({
                    transferData: detailRef.current,
                  });
                }
                const res = await apiStore.markNotificationAsRead({
                  id: item.id,
                });
                if (res) {
                  notificationStore.refreshUserUnreadNotificationCount();
                  fetchPage({ nextPage: 1, replace: true });
                }
              },
              onCancel: () => {
                Dialog.close(pid);
              },
            });
          })
          .catch((err: any) => {
            globalSpinner.close();
            globalToast.show(err?.message || "获取转账详情失败");
          });
      } else {
        Dialog.show({
          cancelText: "关闭",
          confirmText: getConfirmText(item, detailRef.current),
          confirmOnAction: item.is_read === false,
          content: React.createElement(NotificationCenterContent, {
            ...item,
          }),
          onConfirm: async () => {
            const res = await apiStore.markNotificationAsRead({ id: item.id });
            if (res) {
              notificationStore.refreshUserUnreadNotificationCount();
              fetchPage({ nextPage: 1, replace: true });
              globalToast.show("标记已读成功");
            }
          },
        });
      }
    },
    [Dialog, apiStore, fetchPage, notificationStore],
  );

  return (
    <>
      <Container>
        <SectionList
          style={
            { flex: 1, minHeight: 0 ,height:"auto"}
          }
          sections={
            sections as unknown as SectionListData<NoticeItem, NoticeSection>[]
          }
          keyExtractor={(item) => String(item.id)}
          refreshing={refreshing}
          onRefresh={onRefresh}
          onEndReached={onEndReached}
          onEndReachedThreshold={0.3}
          stickySectionHeadersEnabled={false}
          renderSectionHeader={({ section }) => {
            const unreadCount = section.data.filter(
              (x) => x.status === "unread",
            ).length;
            return (
              <BetweenFlex height="auto" width="100%" padding="13px 0">
                <StartFlex width={'auto'} height="auto">
                  <IconFont
                    marginRight="8"
                    name={section.icon}
                    size={16}
                    color={theme.colors.primary}
                  />
                  <Text fontSize="lg" fontWeight="600">
                    {section.label}
                  </Text>
                </StartFlex>
                {unreadCount > 0 && (
                  <CenterFlex
                    width="auto"
                    height="auto"
                    padding="0 10px"
                    bg="#ff3b30"
                    borderRadius="10px"
                  >
                    <Text color="white" lineHeight="16px">
                      {unreadCount}
                    </Text>
                  </CenterFlex>
                )}
              </BetweenFlex>
            );
          }}
          renderItem={({ item, section, index }) => {
            const isFirst = index === 0;
            const isLast = section.data.length - 1 === index;
            return (
              <NoticeRow
                item={item}
                theme={theme}
                icon={section.icon}
                isFirst={isFirst}
                isLast={isLast}
                onClick={() => {
                  onMarkAsRead(item);
                }}
              />
            );
          }}
          ListFooterComponent={() => {
            if (!loadingMore) return null;
            return (
              <CenterFlex padding="12px 0">
                <ActivityIndicator color={theme.colors.primary} />
              </CenterFlex>
            );
          }}
        />
      </Container>
    </>
  );
};

export default observer(NoticeCenter);
